'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowDown, ArrowRight, ArrowUpRight } from '@phosphor-icons/react';
import { PauseIcon, PlayIcon } from '@/components/icons';
import { AboutFoodStory } from './AboutFoodStory';
import { AboutKitchenScene } from './AboutKitchenScene';
import { AboutAppScene } from './AboutAppScene';
import { siteConfig } from '@/config/site';
import './about-experience.css';

export function AboutExperience() {
  const pageRef = useRef<HTMLElement>(null);
  const heroRef = useRef<HTMLElement>(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const root = pageRef.current;
    if (!root || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('ab-revealed');
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12 }
    );
    root.querySelectorAll('[data-ab-reveal]').forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const hero = heroRef.current;
    const media = window.matchMedia(
      '(min-width: 961px) and (prefers-reduced-motion: no-preference)'
    );
    if (!hero) return;
    let frame = 0;
    let visible = true;
    const update = () => {
      frame = 0;
      if (!media.matches || paused || document.hidden || !visible) return;
      const rect = hero.getBoundingClientRect();
      const amount = Math.max(0, Math.min(1, -rect.top / rect.height));
      hero.style.setProperty('--ab-hero-drift', amount * 38 + 'px');
    };
    const schedule = () => {
      if (!frame && visible && !document.hidden) frame = requestAnimationFrame(update);
    };
    const reset = () => {
      hero.style.setProperty('--ab-hero-drift', '0px');
      schedule();
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) schedule();
    });
    observer.observe(hero);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    document.addEventListener('visibilitychange', schedule);
    media.addEventListener('change', reset);
    reset();
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('visibilitychange', schedule);
      media.removeEventListener('change', reset);
    };
  }, [paused]);

  return (
    <main className="ab-page" ref={pageRef}>
      <div className="ab-container">
        <div className="ab-topline">
          <Link href="/">
            Главная <span>/ О нас</span>
          </Link>
          <a href="#ab-story">
            Знакомимся ближе <ArrowDown size={14} />
          </a>
        </div>
        <section className="ab-hero" ref={heroRef} aria-labelledby="ab-title">
          <div className="ab-hero-surface" />
          <div className="ab-hero-copy">
            <span className="ab-eyebrow">Fast casual роллы. Сервис next.</span>
            <h1 id="ab-title">
              Привет.
              <br />
              Мы —<br />
              <span>Азиаточка.</span>
            </h1>
            <p>
              Только роллы. Проверенные ингредиенты.
              <br />И люди, которым не всё равно.
            </p>
            <Link className="ab-button ab-button-white" href="/#menu">
              Выбрать роллы <ArrowUpRight size={20} />
            </Link>
          </div>
          <div className="ab-hero-art">
            <Image
              src="/storefront/about-mascot-hello-v1.webp"
              alt="Персонаж Азиаточки приветствует вас"
              width={900}
              height={1125}
              priority
              sizes="(max-width: 760px) 90vw, (max-width: 960px) 520px, 650px"
            />
          </div>
          <div className="ab-hero-bottom">
            <span>Ростов-на-Дону</span>
            <span>Роллы с характером</span>
          </div>
          <button
            className="ab-motion-control"
            type="button"
            onClick={() => setPaused(!paused)}
            aria-label={paused ? 'Включить движение сцены' : 'Остановить движение сцены'}
            aria-pressed={paused}
          >
            {paused ? <PlayIcon size={15} /> : <PauseIcon size={15} />}
          </button>
        </section>

        <AboutFoodStory />
        <AboutKitchenScene />

        <AboutAppScene />

        <section className="ab-finale" aria-labelledby="ab-finale-title" data-ab-reveal>
          <div className="ab-finale-top">
            <span className="ab-eyebrow">Остаёмся на связи</span>
            <span>Азиаточка × Вы</span>
          </div>
          <div className="ab-finale-main">
            <h2 id="ab-finale-title">
              Рады знакомству,
              <br />
              <span>друзья.</span>
            </h2>
            <div>
              <p>
                Контент, плюшки, немного сарказма и вкусные фото. Отдыхайте, смейтесь, делитесь
                впечатлениями — мы рядом.
              </p>
              <a
                className="ab-vk-link"
                href={siteConfig.links.vk}
                target="_blank"
                rel="noopener noreferrer"
              >
                Присоединиться в VK <ArrowUpRight size={20} />
              </a>
            </div>
          </div>
          <Link href="/#menu" className="ab-finale-menu">
            <span>Теперь можно и поесть.</span>
            <strong>
              К роллам <ArrowRight size={27} />
            </strong>
          </Link>
        </section>
      </div>
    </main>
  );
}
